import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data";

export const Navbar = () => {
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(latest > prev && latest > 200 && !open);
    setScrolled(latest > 40);
  });

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        initial={{ y: -100 }}
        animate={{ y: hidden ? -100 : 0 }}
        transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
          scrolled ? "backdrop-blur-xl bg-background/60 border-b border-foreground/5" : "bg-transparent"
        }`}
      >
        <nav className="flex items-center justify-between px-6 lg:px-12 h-20">
          <button
            data-testid="nav-logo"
            onClick={() => go("#top")}
            className="group flex items-center text-left"
            aria-label="Sixes & Sevens home"
          >
            <img
              src="/logo.webp"
              alt="Sixes & Sevens"
              className="h-14 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </button>

          <div className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="link-underline font-body text-sm uppercase tracking-[0.15em] text-foreground/80 hover:text-foreground transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta"
              onClick={() => go("#contact")}
              className="inline-flex items-center bg-primary text-primary-foreground px-3 md:px-6 py-2 md:py-2.5 text-[10px] md:text-xs uppercase tracking-[0.15em] md:tracking-[0.2em] font-bold hover:bg-foreground transition-colors duration-300"
            >
              Let&apos;s Talk
            </button>

            <button
              data-testid="nav-menu-toggle"
              className="md:hidden grid place-items-center h-10 w-10 border border-foreground/25 text-foreground"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99] bg-background md:hidden flex flex-col justify-center px-8 gap-2 pb-16"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="font-display font-extrabold text-4xl uppercase tracking-tight text-left py-1.5"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.a
              data-testid="mobile-cta"
              href="mailto:sixesandsevens01@gmail.com"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + NAV_LINKS.length * 0.06 }}
              className="mt-6 inline-flex w-fit items-center bg-primary text-primary-foreground px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold"
            >
              sixesandsevens01@gmail.com
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
